import { lazy, Suspense } from 'react'

import { AnimatePresence } from 'framer-motion'
import { Navigate, Route, Routes } from 'react-router'

import AdminMainLayout from './layouts/AdminMain'
import ClientMainLayout from './layouts/ClientMain'
import MinimalLayout from './layouts/Minimal'
import AdminAudiencesView from './views/Admin/Audiences'
import AdminEventsView from './views/Admin/Events'
import AdminMembersView from './views/Admin/Members'
import AdminSettingsView from './views/Admin/Settings'
import AdminMemberSettingsView from './views/Admin/Settings/MemberSettings/MemberSettings'
import AdminNotificationSettingsView from './views/Admin/Settings/NotificationSettings'
import AdminRichMenuSettingsView from './views/Admin/Settings/RichMenuSettings'
import AdminSystemSettingsView from './views/Admin/Settings/SystemSettings'
const AdminEventSettingsView = lazy(
	() => import('./views/Admin/Settings/EventSettings/EventSettings'),
)

import { ErrorBoundaryComponent, TopProgressBar } from '@/components'
import { RichMenuSettingsProvider } from '@/components/admin/Settings/RichMenu/RichMenuSettingsProvider'
import { LayoutConfigProvider } from '@/providers/layoutConfig.provider'
import { COMMONS } from '@/utils'

const AdminLoginView = lazy(() => import('./views/Admin/Login'))
const AdminCouponsView = lazy(() => import('./views/Admin/Coupons'))
const AdminDevCronView = lazy(() => import('./views/Admin/DevCron'))
// const AdminHomeView = lazy(() => import("./views/Admin/Home"))
const AdminHomeV2View = lazy(() => import('./views/Admin/HomeV2'))
// const AdminCategoriesView = lazy(() => import('./views/Admin/Categories'))

// const EventDetailView = lazy(() => import('./views/Admin/EventDetail'))

// const AdminCampaignsView = lazy(() => import('./views/Admin/Campaigns'))
const AdminListCampaignsView = lazy(() => import('./views/Admin/ListCampaigns'))
const AdminAddCampaignView = lazy(() => import('./views/Admin/AddCampaign'))
const AdminEditCampaignView = lazy(() => import('./views/Admin/EditCampaign'))
const AdminCopyCampaignView = lazy(() => import('./views/Admin/CopyCampaign'))

// const AdminCampaignDetaillView = lazy(() => import('./views/Admin/CampaignDetail/CampaignDetail'))

const ClientLoginView = lazy(() => import('./views/Client/Login'))
const ClientRegisterView = lazy(() => import('./views/Client/Register'))
const ClientProfileUpdateView = lazy(() => import('./views/Client/ProfileUpdate'))
const ClientMembershipView = lazy(() => import('./views/Client/Membership'))
const ClientRegistrationsView = lazy(() => import('./views/Client/Registrations'))
const ClientRegistrationDetailView = lazy(() => import('./views/Client/RegistrationDetail'))
const ClientEventsView = lazy(() => import('./views/Client/Events'))
const ClientEventDetailView = lazy(() => import('./views/Client/EventDetail'))

const PaymentRequestView = lazy(() => import('./views/Public/Payment/PaymentRequest'))

const LineAccessView = lazy(() => import('./views/LineAccess'))
const LineFriendView = lazy(() => import('./views/LineFriend'))
const PermissionErrorView = lazy(() => import('./views/PermissionError'))
const NotFoundView = lazy(() => import('./views/NotFound'))
const LiffStateRedirect = lazy(() => import('./views/Client/LiffStateRedirect'))

const Router = () => {
	return (
		<ErrorBoundaryComponent>
			<Suspense fallback={<TopProgressBar />}>
				<AnimatePresence mode='wait'>
					<LayoutConfigProvider>
						<Routes>
							<Route
								path='/'
								element={<Navigate replace to={COMMONS.ADMIN_LOGIN_ROUTE} />}
							/>
							<Route
								path={COMMONS.ADMIN_LOGIN_ROUTE}
								element={
									<MinimalLayout>
										<AdminLoginView />
									</MinimalLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_MEMBERS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminMembersView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_EVENTS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminEventsView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_COUPONS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminCouponsView />
									</AdminMainLayout>
								}
							/>

							<Route
								path={COMMONS.ADMIN_AUDIENCES_ROUTE}
								element={
									<AdminMainLayout>
										<AdminAudiencesView />
									</AdminMainLayout>
								}
							/>
							<Route
								path='/campaigns'
								element={
									<AdminMainLayout>
										<AdminListCampaignsView />
									</AdminMainLayout>
								}
							/>
							<Route
								path='/campaigns/add'
								element={
									<AdminMainLayout>
										<AdminAddCampaignView />
									</AdminMainLayout>
								}
							/>
							<Route
								path='/campaigns/copy/:campaignId'
								element={
									<AdminMainLayout>
										<AdminCopyCampaignView />
									</AdminMainLayout>
								}
							/>
							<Route
								path='/campaigns/:campaignId'
								element={
									<AdminMainLayout>
										<AdminEditCampaignView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_SETTINGS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminSettingsView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_SETTINGS_SYS_ROUTE}
								element={
									<AdminMainLayout>
										<AdminSystemSettingsView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_SETTINGS_EVENT_ROUTE}
								element={
									<AdminMainLayout>
										<AdminEventSettingsView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_SETTINGS_CUSTOM_REG_ROUTE}
								element={
									<AdminMainLayout>
										<AdminMemberSettingsView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_SETTINGS_NOTIFICATION_ROUTE}
								element={
									<AdminMainLayout>
										<AdminNotificationSettingsView />
									</AdminMainLayout>
								}
							/>
							<Route
								path={COMMONS.ADMIN_SETTINGS_RICHMENU_ROUTE}
								element={
									<RichMenuSettingsProvider>
										<AdminMainLayout>
											<AdminRichMenuSettingsView />
										</AdminMainLayout>
									</RichMenuSettingsProvider>
								}
							/>
							<Route
								path={COMMONS.ADMIN_DEV_CRON_ROUTE}
								element={
									<MinimalLayout>
										<AdminDevCronView />
									</MinimalLayout>
								}
							/>
							<Route
								path={COMMONS.CLIENT_LOGIN_ROUTE}
								element={
									<MinimalLayout>
										<ClientLoginView />
									</MinimalLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_LOGIN_ROUTE}/:liffId`}
								element={
									<MinimalLayout>
										<ClientLoginView />
									</MinimalLayout>
								}
							/>
							{/* Handle malformed LIFF URLs where liff.state gets appended to /liff/login */}
							{/* e.g., /liff/login/liff/register -> /liff/register/:liffId */}
							<Route
								path={`${COMMONS.CLIENT_LOGIN_ROUTE}/liff/*`}
								element={<LiffStateRedirect />}
							/>
							<Route
								path={COMMONS.CLIENT_REGISTER_ROUTE}
								element={
									<MinimalLayout>
										<ClientRegisterView />
									</MinimalLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_REGISTER_ROUTE}/:liffId`}
								element={
									<MinimalLayout>
										<ClientRegisterView />
									</MinimalLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_PROFILE_UPDATE_ROUTE}/:liffId`}
								element={
									<ClientMainLayout>
										<ClientProfileUpdateView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_MEMBERSHIP_ROUTE}/:liffId`}
								element={
									<ClientMainLayout>
										<ClientMembershipView />
									</ClientMainLayout>
								}
							/>
							{/* Tickets route - redirects to membership where cafe tickets are displayed */}
							<Route
								path={`${COMMONS.CLIENT_TICKETS_ROUTE}/:liffId`}
								element={
									<ClientMainLayout>
										<ClientMembershipView />
									</ClientMainLayout>
								}
							/>

							<Route
								path={`${COMMONS.CLIENT_REGISTRATIONS_ROUTE}/:liffId`}
								element={
									<ClientMainLayout>
										<ClientRegistrationsView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_REGISTRATION_DETAIL_ROUTE}/:liffId/:registrationId`}
								element={
									<ClientMainLayout>
										<ClientRegistrationDetailView />
									</ClientMainLayout>
								}
							/>

							<Route
								path={`${COMMONS.CLIENT_EVENTS_ROUTE}`}
								element={
									<ClientMainLayout>
										<ClientEventsView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_EVENTS_ROUTE}/:liffId`}
								element={
									<ClientMainLayout>
										<ClientEventsView />
									</ClientMainLayout>
								}
							/>
							<Route
								path={`${COMMONS.CLIENT_EVENT_DETAIL_ROUTE}/:liffId/:eventId`}
								element={
									<ClientMainLayout>
										<ClientEventDetailView />
									</ClientMainLayout>
								}
							/>

							<Route
								path={COMMONS.LINE_ACCESS_ROUTE}
								element={
									<MinimalLayout>
										<LineAccessView />
									</MinimalLayout>
								}
							/>
							<Route
								path='/payment' // Explicit public route
								element={
									<MinimalLayout>
										<PaymentRequestView />
									</MinimalLayout>
								}
							/>
							<Route
								path={COMMONS.LINE_FRIEND_ROUTE}
								element={
									<MinimalLayout>
										<LineFriendView />
									</MinimalLayout>
								}
							/>
							<Route
								path={COMMONS.PERMISSION_ERROR_ROUTE}
								element={
									<MinimalLayout>
										<PermissionErrorView />
									</MinimalLayout>
								}
							/>
							<Route
								path={COMMONS.NOT_FOUND_ROUTE}
								element={
									<MinimalLayout>
										<NotFoundView />
									</MinimalLayout>
								}
							/>
							<Route
								path='*'
								element={<Navigate replace to={COMMONS.NOT_FOUND_ROUTE} />}
							/>
						</Routes>
					</LayoutConfigProvider>
				</AnimatePresence>
			</Suspense>
		</ErrorBoundaryComponent>
	)
}

export default Router
